import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { startStripeCheckout } from "../lib/payments";
import { toast } from "sonner";
import { Crown, FileText, Palette, Type, Shield, ChevronRight } from "lucide-react";

export default function License() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/license/me").then((r) => setMe(r.data)).catch(() => setMe(null));
  }, []);

  const apply = async () => {
    if (!me?.is_authenticated) {
      toast.info("Create an account to license the brand.");
      navigate("/register?next=/license");
      return;
    }
    setBusy(true);
    try {
      await startStripeCheckout({
        package_id: "brand_license_annual",
        rail: "stripe",
        metadata: { license_purchase: "true", customer_id: me.user_id || "", email: me.email || "" },
        success_path: "/license/success",
        cancel_path: "/license",
      });
      // Redirects to Stripe
    } catch (err) {
      setBusy(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
    }
  };

  return (
    <div className="max-w-[1180px] mx-auto px-6 lg:px-10 pt-32 pb-24" data-testid="license-page">
      {/* Hero */}
      <div className="mb-16 reveal">
        <p className="eyebrow mb-4">Brand Kit · Annual License · 2026</p>
        <h1 className="font-display text-6xl md:text-8xl leading-[0.92] mb-8" style={{ letterSpacing: "-0.02em" }}>
          License the WealthFirst <em style={{ color: "var(--brass)" }}>brand</em>.
        </h1>
        <p className="lede max-w-[760px]">
          The brass-on-ink palette, the Cormorant + Manrope system, the monogram, the trade dress — all of it.
          Annual recurring license for franchise affiliates, content partners, regional broadcast partners,
          and white-label distribution.
        </p>
      </div>

      {me?.license_active && (
        <div className="mb-12 p-6" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }} data-testid="license-active-banner">
          <p className="eyebrow mb-2" style={{ color: "var(--brass)" }}>You hold an active license</p>
          <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
            Active until {me.expires_at?.slice(0, 10)}. Access your downloadable brand kit at{" "}
            <Link to="/license/dashboard" className="brass-link">/license/dashboard</Link>.
          </p>
        </div>
      )}

      <hr className="hairline mb-16" />

      {/* What's Included */}
      <section className="mb-16">
        <p className="eyebrow mb-4">01 · What's Included</p>
        <h2 className="font-display text-4xl md:text-5xl mb-10 leading-tight">A complete brand kit — <em style={{ color: "var(--brass)" }}>annually licensed</em>.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Crown, title: "Mark Suite", desc: "Vector monogram + horizontal lockup in SVG, PNG (transparent, 4 sizes), and print-ready PDF." },
            { icon: Palette, title: "Color System", desc: "The 6-token brass-and-ink palette with HEX, RGB, CMYK, and Pantone-equivalent values." },
            { icon: Type, title: "Typography", desc: "Licensed pairings of Cormorant Garamond + Manrope, with sizing scale and tracking rules." },
            { icon: FileText, title: "Brand Guidelines PDF", desc: "44-page guidelines document covering co-branding, signage, broadcast lower-thirds, and partnership lockups." },
            { icon: Shield, title: "Trade-Name License", desc: "Limited right to use the 'WealthFirst' trade name + 'A WealthFirst Affiliate' lockup across one designated business." },
            { icon: ChevronRight, title: "Concierge Review", desc: "Quarterly brand-compliance check by our editorial team. We review your applications and flag deviations." },
            { icon: Crown, title: "Marketing Templates", desc: "Press release template, partnership announcement template, social tile templates (Instagram, LinkedIn, X)." },
            { icon: Shield, title: "30-Day Grace Renewal", desc: "Email reminders 30, 14, and 7 days before expiry. Your license remains active during the renewal window." },
          ].map((item) => (
            <div key={item.title} className="p-6" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}>
              <item.icon size={22} style={{ color: "var(--brass)" }} className="mb-4" />
              <h3 className="font-display text-xl mb-2 leading-tight">{item.title}</h3>
              <p className="text-sm" style={{ color: "var(--ink-muted)", lineHeight: 1.6 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <hr className="hairline mb-16" />

      {/* Pricing */}
      <section className="mb-16">
        <p className="eyebrow mb-4">02 · Annual License</p>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-display text-5xl md:text-6xl leading-tight mb-6">
              One fee. <em style={{ color: "var(--brass)" }}>Twelve months</em>. Full rights.
            </h2>
            <p className="lede mb-8">
              Pay once per year. No usage caps. No revenue-share. No surprise add-ons. Revocable only for misuse
              of the marks per the License Agreement (Section 7).
            </p>
            <ul className="text-sm space-y-3" style={{ color: "var(--ink-muted)", lineHeight: 1.6 }}>
              <li><span style={{ color: "var(--brass)" }}>—</span> Renewable annually, 30-day grace period.</li>
              <li><span style={{ color: "var(--brass)" }}>—</span> Includes all future asset updates released during the license term.</li>
              <li><span style={{ color: "var(--brass)" }}>—</span> Transferable to a parent entity with written consent.</li>
              <li><span style={{ color: "var(--brass)" }}>—</span> Not assignable to competitors of WEALTH FIRST INC.</li>
            </ul>
          </div>
          <div className="lg:col-span-5 p-8" style={{ border: "1px solid var(--brass)", background: "var(--surface)", position: "relative" }}>
            <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at top right, rgba(194, 163, 115, 0.08), transparent 50%)", pointerEvents: "none" }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <p className="eyebrow mb-3" style={{ color: "var(--brass)" }}>Annual License · Recurring</p>
              <div className="font-display leading-none mb-2" style={{ fontSize: "84px", color: "var(--brass)" }}>
                <sup style={{ fontSize: "28px", verticalAlign: "super", marginRight: "4px" }}>$</sup>4,500
                <span className="text-sm tracking-[0.18em] uppercase ml-2" style={{ color: "var(--ink-muted)" }}>/ year</span>
              </div>
              <p className="text-xs uppercase tracking-[0.18em] mb-8" style={{ color: "var(--ink-faint)" }}>
                Billed once per year · USD
              </p>
              <button
                onClick={apply}
                disabled={busy || me?.license_active}
                className="btn-brass w-full"
                style={{ justifyContent: "center", padding: "16px 0" }}
                data-testid="license-apply-button"
              >
                {busy ? "Redirecting…" : me?.license_active ? "License Active" : "Apply for License →"}
              </button>
              <p className="text-[11px] mt-4 text-center" style={{ color: "var(--ink-faint)" }}>
                Secure checkout via Stripe · Visa · MC · Amex · ACH · Klarna · Apple/Google Pay
              </p>
            </div>
          </div>
        </div>
      </section>

      <hr className="hairline mb-16" />

      {/* Brand-kit preview link */}
      <section className="mb-16">
        <p className="eyebrow mb-4">03 · Preview the Kit</p>
        <h2 className="font-display text-4xl md:text-5xl mb-6 leading-tight">
          See it before you license it.
        </h2>
        <p className="lede mb-8 max-w-[680px]">
          The full standalone brand-kit reference, with logo marks, the brass palette, the typographic system,
          and approved-vs-prohibited usage standards — viewable publicly. Downloadable assets are reserved for licensed partners.
        </p>
        <a
          href="/logo.html"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 px-6 py-3 text-xs uppercase tracking-[0.18em]"
          style={{ border: "1px solid var(--brass)", color: "var(--brass)" }}
          data-testid="logo-html-link"
        >
          Open Brand Kit Preview <ChevronRight size={14} />
        </a>
      </section>
    </div>
  );
}
